import { CircleAlert, CircleCheck } from 'lucide-react'

import { StatCard } from '@/components/stat/StatCard'
import { Card, CardContent } from '@/components/ui/card'
import { signedTon } from '@/features/report/reconcile/format'
import { formatInteger } from '@/lib/format'
import { TONNAGE_GROUPS, formatTon, type TonnageGroup, type TonnageSummary } from '@/lib/kantar-dia'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

const t = messages.reconcile.tonnage

const groupDiff = (group: TonnageGroup) => group.diaKg - group.kantarKg

/**
 * Farkı açıklayan cümleler; her grup kendi payını (tartı, ton, kural) söyler.
 * Toplamı tam verir: eşleşen fark + yalnız DİA'dakiler - yalnız kantardakiler.
 */
function causes(summary: TonnageSummary): { text: string; ok: boolean }[] {
  const { matched, kantarOnly, diaOnly } = summary
  const lines: { text: string; ok: boolean }[] = []
  if (matched.count > 0) {
    const diff = groupDiff(matched)
    const count = formatInteger(matched.count)
    const tons = formatTon(matched.kantarKg)
    lines.push(
      diff === 0
        ? { text: t.matchedOk(count, tons), ok: true }
        : { text: t.matchedOff(count, signedTon(diff)), ok: false },
    )
  }
  if (kantarOnly.count > 0) {
    const text = t.kantarOnlyCause(formatInteger(kantarOnly.count), formatTon(kantarOnly.kantarKg))
    lines.push({ text, ok: false })
  }
  if (diaOnly.count > 0) {
    const text = t.diaOnlyCause(formatInteger(diaOnly.count), formatTon(diaOnly.diaKg))
    lines.push({ text, ok: false })
  }
  return lines
}

/** Kantar ve DİA tonaj toplamları, aradaki fark ve farkın kaynağı (tıklanacak bir şey yok). */
export function TonnageCard({
  summary,
  recordCount,
}: {
  summary: TonnageSummary
  /** Sayfadaki tüm tartılar ("Toplam tartı"); tonajı olmayanlar karşılaştırmaya girmez. */
  recordCount: number
}) {
  const balanced = summary.diffKg === 0 && groupDiff(summary.matched) === 0
  const compared = TONNAGE_GROUPS.reduce((sum, key) => sum + summary[key].count, 0)
  const withoutTonnage = recordCount - compared

  return (
    <section aria-labelledby="tonnage-title" className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 id="tonnage-title" className="text-foreground text-base font-medium">
          {t.title}
        </h2>
        <p className="text-text-secondary leading-body text-sm">{t.description}</p>
      </div>
      {/* Toplamlar uiarc metric-card (`StatCard`); fark yoksa açıklama farkın bağlam satırında. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatCard
          label={t.kantar}
          value={formatTon(summary.kantarKg)}
          hint={t.count(formatInteger(summary.kantarCount))}
        />
        <StatCard
          label={t.dia}
          value={formatTon(summary.diaKg)}
          hint={t.count(formatInteger(summary.diaCount))}
        />
        <StatCard
          label={t.diff}
          icon={
            balanced ? (
              <CircleCheck className="text-success" aria-hidden="true" />
            ) : (
              <CircleAlert className="text-warning" aria-hidden="true" />
            )
          }
          value={signedTon(summary.diffKg)}
          valueClassName={summary.diffKg !== 0 ? 'text-destructive' : undefined}
          hint={balanced ? t.balanced : undefined}
        />
      </div>
      {(!balanced || withoutTonnage > 0) && (
        <Card size="sm">
          <CardContent className="flex flex-col gap-3">
            {!balanced && (
              <ul className="flex flex-col gap-1.5 text-sm">
                {causes(summary).map(({ text, ok }) => {
                  const Icon = ok ? CircleCheck : CircleAlert
                  return (
                    <li key={text} className="flex items-start gap-1.5">
                      <Icon
                        className={cn(
                          'mt-0.5 size-4 shrink-0',
                          ok ? 'text-success' : 'text-warning',
                        )}
                        aria-hidden="true"
                      />
                      {text}
                    </li>
                  )
                })}
              </ul>
            )}
            {withoutTonnage > 0 && (
              <p
                className={cn(
                  'text-text-muted leading-body text-sm',
                  !balanced && 'border-border border-t pt-3',
                )}
              >
                {t.withoutTonnage(formatInteger(withoutTonnage), formatInteger(recordCount))}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </section>
  )
}
